import { FC } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { faPhotoFilm } from '@fortawesome/pro-regular-svg-icons/faPhotoFilm';
import { faSwatchbook } from '@fortawesome/pro-regular-svg-icons/faSwatchbook';
import { faFileChartPie } from '@fortawesome/pro-regular-svg-icons/faFileChartPie';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';
import { faFolder } from '@fortawesome/pro-regular-svg-icons/faFolder';
import { faSuitcase } from '@fortawesome/pro-regular-svg-icons/faSuitcase';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faFeather } from '@fortawesome/pro-regular-svg-icons/faFeather';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons/faUserPlus';
import { UserPermission } from 'constants/userPermission';
import { resolveLabel } from 'actions';
import routes, { modalRoutes } from 'constants/routes';
import { useActions } from 'app/hooks/useActions';
import { openUrl } from 'utils/url';
import useSettingsV2Gate from 'app/hooks/useSettingsV2Gate';
import { globalProfilesUrl } from 'app/config/settingsV2Mode';
import FeaturedCreateOption from 'components/navigation/verticalNavigation/mainMenu/createTooltipContent/FeaturedCreateOption';
import RegularCreateOption from 'components/navigation/verticalNavigation/mainMenu/createTooltipContent/RegularCreateOption';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { usePostsCreateNew } from 'app/modules/posts/hooks/usePostsCreateNew';
import { useUser } from 'modules/user/userSelector';
import { hasPermissions } from '@kontentino/react-permissions-gate';

export type CreateOption = {
  key: string;
  label: string;
  description?: string;
  icon: IconDefinition;
  isVisible: boolean;
  onClick: () => void;
  highlighted?: boolean;
};

type Props = {
  onRequestClose?: () => void;
};

const CreateTooltipContent: FC<Props> = ({ onRequestClose }) => {
  const { isSettingsV2Enabled } = useSettingsV2Gate();
  const { t } = useTranslation();
  const { permissionsKeys } = useUser();
  const { profileActions } = useActions('add-new-dropdown');
  const { userActions } = useActions('users');
  const navigate = useNavigate();
  const location = useLocation();
  const { getCreateInspirationUrl, getCreatePostUrl, isInPostsModule } =
    usePostsCreateNew();

  const isItemVisible = (permissions: UserPermission[]) =>
    hasPermissions(permissionsKeys, permissions);

  const featuredOptions = [
    {
      key: 'post',
      label: t('post_one'),
      description:
        'Schedule and publish content across all your social channels.',
      icon: faPhotoFilm,
      isVisible: isItemVisible([UserPermission.POST_CREATE]),
      highlighted: true,
      onClick: () => navigate(getCreatePostUrl()),
    },
    {
      key: 'brand',
      label: t('brandHub.sidebarLabel', 'Brand'),
      description: 'Define your brand voice, visuals, and content guidelines.',
      icon: faSwatchbook,
      isVisible: isItemVisible([UserPermission.POST_CREATE]),
      onClick: () =>
        navigate({
          pathname: routes.BRAND_HUB,
          hash: modalRoutes.BRAND_HUB.MANAGE_PROFILE,
        }),
    },
    {
      key: 'report',
      label: t('report_one', 'Report'),
      description: 'Track performance metrics and generate analytics reports.',
      icon: faFileChartPie,
      isVisible: isItemVisible([UserPermission.REPORT_CREATE]),
      onClick: () => {
        navigate({
          pathname: routes.NEW_REPORT,
        });
      },
    },
  ].filter((option) => option.isVisible);

  const regularOptions = [
    {
      key: 'social-profile',
      label: t('addProfile'),
      icon: faShareNodes,
      isVisible: isItemVisible([profileActions.add.scopes]),
      highlighted: true,
      onClick: () => {
        if (
          isInPostsModule() ||
          location.pathname.startsWith(routes.INSIGHTS)
        ) {
          profileActions.add.perform();
        } else {
          navigate({
            pathname: routes.POSTS_CALENDAR,
            hash: modalRoutes.NEW_PROFILE,
          });
        }
      },
    },
    {
      key: 'profile-group',
      label: t('group'),
      icon: faFolder,
      isVisible: isItemVisible([UserPermission.PAGE_PRESETS_MANAGE]),
      onClick: () => {
        navigate({
          pathname: isInPostsModule() ? undefined : routes.POSTS_CALENDAR,
          hash: modalRoutes.MANAGE_PROFILE_GROUP,
        });
      },
    },
    {
      key: 'content-plan',
      label: t('contentPlan'),
      icon: faSuitcase,
      isVisible: isItemVisible([UserPermission.PROJECT_CREATE]),
      onClick: () =>
        navigate({
          hash: modalRoutes.NEW_CONTENT_PLAN,
        }),
    },
    {
      key: 'global-profile',
      label: t('globalProfile', 'Global profile'),
      icon: faGlobe,
      isVisible: isItemVisible([UserPermission.ACCESS_USERS]),
      onClick: () => openUrl(globalProfilesUrl(isSettingsV2Enabled)),
    },
    {
      key: 'inspiration',
      label: t('inspiration'),
      icon: faFeather,
      isVisible: isItemVisible([UserPermission.INSPIRATION_CREATE]),
      onClick: () => navigate(getCreateInspirationUrl()),
    },
    {
      key: 'user',
      label: resolveLabel(userActions.inviteUser.label),
      icon: faUserPlus,
      isVisible: isItemVisible(userActions.inviteUser.scopes),
      onClick: userActions.inviteUser.perform,
    },
  ].filter((option) => option.isVisible);

  const gridColsClass = (count: number) =>
    clsx({
      'tw-grid-cols-3': count >= 3,
      'tw-grid-cols-2': count === 2,
      'tw-grid-cols-1': count <= 1,
    });

  return (
    <div className="tw-flex tw-max-w-[752px] tw-flex-col tw-gap-4">
      {featuredOptions.length > 0 && (
        <div
          className={clsx(
            'tw-grid',
            gridColsClass(featuredOptions.length),
            'tw-gap-5',
          )}
        >
          {featuredOptions.map((option, index) => (
            <FeaturedCreateOption
              key={`create-featured-${option.key}`}
              option={{
                ...option,
                onClick: () => {
                  option.onClick();
                  onRequestClose?.();
                },
              }}
            />
          ))}
        </div>
      )}

      {regularOptions.length > 0 && (
        <div
          className={clsx(
            'tw-grid',
            gridColsClass(regularOptions.length),
            'tw-gap-x-5 tw-gap-y-2',
          )}
        >
          {regularOptions.map((option) => (
            <RegularCreateOption
              key={`create-${option.key}`}
              option={{
                ...option,
                onClick: () => {
                  option.onClick();
                  onRequestClose?.();
                },
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CreateTooltipContent;
